// CardScheduleSetting.tsx
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button, Checkbox, Container, Dropdown, Icon } from "semantic-ui-react";

import moment, { Moment } from "moment";
import { SlotInfo } from "react-big-calendar";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import SnackMessage from "react-lib/apps/common/SnackMessage";

import CalendarSchedule from "./CalendarSchedule";
import ModAddProvider from "./ModAddProvider";
import { ModEditScheduleProps } from "./ModEditSchedule";
import ModScheduleErrMessage from "./ModScheduleErrMessage";
import { ModScheduleInfoProps } from "./ModScheduleInfo";
import SearchProviderSpecialty from "./SearchProviderSpecialty";
import WeekListSidebar, { WeekRange } from "./WeekListSidebar";

import {
  RunSequence as HolidaySettingRunSequence,
  State as HolidaySettingState,
} from "./sequence/HolidaySetting";
import {
  ACTIONS,
  AddProviderParamsType,
  BTN_ACTS,
  FilterScheduleType,
  formatEventsForWeekView,
  getSelectedDateRanges,
  getYearWeekRange,
  groupEventsByDayAndTime,
  isDateInSelectedRanges,
  MasterOptions,
  PickedState,
  ProviderDetailType,
  RunSequence as ScheduleSettingRunSequence,
  State as ScheduleSettingState,
  SEQUENCE_NAME,
  SetProp,
} from "./sequence/ScheduleSetting";

type CardScheduleSettingProps = Pick<
  PickedState,
  | "buttonLoadCheck"
  | "errorMessage"
  | "patientHNChatList"
  | "searchedItemListWithKey"
  | "selectedDivision"
  | "successMessage"
> & {
  onEvent: (e: any) => void;
  setProp: SetProp;
  runSequence: HolidaySettingRunSequence & ScheduleSettingRunSequence;
  HolidaySettingSequence: HolidaySettingState["HolidaySettingSequence"];
  ScheduleSettingSequence: ScheduleSettingState["ScheduleSettingSequence"];
  isProvider?: boolean;
  masterOptions: MasterOptions;
};

type RangeDateType = {
  end: Moment;
  start: Moment;
};

type RangeWeekType = {
  end: string;
  start: string;
};

type Styles = Record<"btnAdd", CSSProperties>;

const IMAGES = {
  provider: "/static/images/schedule/provider-icon.png",
  publishedActive: "/static/images/schedule/published-schedule-active-icon.png",
};

const styles: Styles = {
  btnAdd: {
    alignItems: "center",
    backgroundColor: "white",
    border: "1px solid var(--primary-theme-color)",
    borderRadius: "3px",
    color: "var(--primary-theme-color)",
    display: "flex",
    padding: "4px 14px",
  },
};

const DATE_FORMAT = "YYYY-MM-DD";

const CURRENT_YEAR = moment().year();

const getStartOfWeek = (weekStartDay = 1) => moment().isoWeekday(weekStartDay).startOf("day");

const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((year) => ({
  key: year,
  text: year,
  value: year,
}));

const CardScheduleSetting = (props: CardScheduleSettingProps) => {
  const weekStartDay = props.ScheduleSettingSequence?.weekStartDay ?? 1;

  const [year, setYear] = useState(CURRENT_YEAR);

  const START_OF_WEEK = useMemo(() => getStartOfWeek(weekStartDay), [weekStartDay]);
  const { firstWeek, lastWeek } = useMemo(
    () => getYearWeekRange(year, weekStartDay),
    [weekStartDay, year]
  );

  const [rangeDate, setRangeDate] = useState<RangeDateType>({
    end: START_OF_WEEK.clone().add(6, "days"),
    start: START_OF_WEEK.clone(),
  });
  const [rangeWeek, setRangeWeek] = useState<RangeWeekType>({
    end: lastWeek,
    start: firstWeek,
  });

  const [selectedWeeks, setSelectedWeeks] = useState<string[]>([START_OF_WEEK.format(DATE_FORMAT)]);
  const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);

  const [openAddProvider, setOpenAddProvider] = useState<boolean>(false);
  const [openModPastDate, setOpenModPastDate] = useState(false);

  const patientHNChatListRef = useRef<string[]>([]);

  useEffect(() => {
    props.runSequence({
      sequence: "ScheduleSetting",
      filter: props.isProvider ? { state: 2 } : {},
      restart: true,
    });

    props.runSequence({ sequence: "HolidaySetting", restart: true });
  }, [props.selectedDivision?.id]);

  useEffect(() => {
    patientHNChatListRef.current = props.patientHNChatList ?? [];
  }, [props.patientHNChatList]);

  // Update rangeWeek เมื่อเปลี่ยนปีหรือ weekStartDay
  useEffect(() => {
    const { firstWeek: updateFirstWeek, lastWeek: updateLastWeek } = getYearWeekRange(
      year,
      weekStartDay
    );

    setRangeWeek({
      end: updateLastWeek,
      start: updateFirstWeek,
    });
  }, [weekStartDay, year]);

  const weekRanges = useMemo((): WeekRange[] => {
    const weeks: WeekRange[] = [];
    const startOfYear = moment().year(year).startOf("year");
    const endOfYear = moment().year(year).endOf("year");

    const currentWeekStart = startOfYear.clone().isoWeekday(weekStartDay);

    // ถ้าวันแรกของปีอยู่หลัง weekStartDay ให้ถอยหลังไปสัปดาห์ก่อนหน้า
    if (currentWeekStart.isAfter(startOfYear)) {
      currentWeekStart.subtract(1, "week");
    }

    while (currentWeekStart.isBefore(endOfYear) || currentWeekStart.isSame(endOfYear, "day")) {
      const weekEnd = currentWeekStart.clone().add(6, "days");
      const startMonth = currentWeekStart.format("MMM");
      const endMonth = weekEnd.format("MMM");
      const startDay = currentWeekStart.format("D");
      const endDay = weekEnd.format("D");
      const startYear = currentWeekStart.format("YYYY");
      const endYear = weekEnd.format("YYYY");

      const isSameMonth = startMonth === endMonth;
      const isSameYear = startYear === endYear;

      const text = isSameMonth
        ? `${startMonth}. ${startDay} - ${endDay}`
        : `${startMonth}. ${startDay} - ${endMonth}. ${endDay}`;

      let textWithYear = "";

      if (isSameYear) {
        textWithYear = `${text} , ${startYear}`;
      } else if (isSameMonth) {
        textWithYear = `${startMonth}. ${startDay} , ${startYear} - ${endDay} , ${endYear}`;
      } else {
        textWithYear = `${startMonth}. ${startDay} , ${startYear} - ${endMonth}. ${endDay} , ${endYear}`;
      }

      const value = currentWeekStart.format(DATE_FORMAT);

      weeks.push({
        endDate: weekEnd.clone(),
        key: value,
        startDate: currentWeekStart.clone(),
        text,
        textWithYear,
        value: currentWeekStart.format(DATE_FORMAT),
      });

      currentWeekStart.add(1, "week");
    }

    return weeks;
  }, [weekStartDay, year]);

  const selectedWeekRange = useMemo(() => {
    const selected = weekRanges.filter((w) => selectedWeeks.includes(w.value));

    if (selected.length === 0) {
      return { endWeek: "", startWeek: "" };
    }

    const fWeek = selected[0];
    const lWeek = selected.length > 1 ? selected[selected.length - 1] : null;

    return {
      endWeek: lWeek?.textWithYear || "",
      startWeek: fWeek.textWithYear,
    };
  }, [selectedWeeks, weekRanges]);

  // Filter weekRanges ตาม rangeWeek
  const filteredWeekRanges = useMemo(() => {
    if (!rangeWeek.start || !rangeWeek.end) {
      return weekRanges;
    }

    const startIndex = weekRanges.findIndex((w) => w.value === rangeWeek.start);
    const endIndex = weekRanges.findIndex((w) => w.value === rangeWeek.end);

    if (startIndex === -1 || endIndex === -1) {
      return weekRanges;
    }

    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);

    return weekRanges.slice(start, end + 1);
  }, [rangeWeek, weekRanges]);

  // เช็คว่าเลือกหลาย weeks หรือไม่
  const isMultipleWeeks = useMemo(() => selectedWeeks.length > 1, [selectedWeeks]);

  const providers = useMemo(
    () =>
      (props.ScheduleSettingSequence?.filterSchedule?.providers ?? []).map(
        (provider) => provider.id
      ),
    [props.ScheduleSettingSequence?.filterSchedule]
  );

  const filteredEvents = useMemo(() => {
    if (selectedWeeks.length === 0) {
      return [];
    }

    const selectedDateRanges = getSelectedDateRanges(selectedWeeks, weekStartDay);
    const events = props.ScheduleSettingSequence?.events ?? [];
    const specialties = props.ScheduleSettingSequence?.filterSchedule?.specialties ?? [];

    const hasSpecialty = (provider: ProviderDetailType) =>
      provider.dsb.specialty_id_list.some((specialtyId: number) =>
        specialties.includes(specialtyId)
      );

    return events.filter((event) => {
      const eventDate = moment(event.start);

      // ตรวจสอบว่าวันที่อยู่ใน range ที่เลือกหรือไม่
      const isDateInRange = isDateInSelectedRanges(eventDate, selectedDateRanges);

      // ถ้าไม่อยู่ในช่วงวันที่ที่เลือก ให้ข้ามเลย
      if (!isDateInRange) {
        return false;
      }

      // ตรวจสอบ provider filter
      const hasProviderFilter = providers.length > 0;
      const hasMatchingProvider = hasProviderFilter
        ? event.providers.some((p) => providers.includes(p.doctor_provider_id))
        : true;

      // ตรวจสอบ specialty filter
      const hasSpecialtyFilter = specialties.length > 0;
      const hasMatchingSpecialty = hasSpecialtyFilter
        ? event.providers.some((element) => hasSpecialty(element))
        : true;

      return hasMatchingProvider && hasMatchingSpecialty;
    });
  }, [
    props.ScheduleSettingSequence?.events,
    props.ScheduleSettingSequence?.filterSchedule,
    providers,
    selectedWeeks,
    weekStartDay,
  ]);

  const formattedEvents = useMemo(() => {
    const specialties = props.ScheduleSettingSequence?.filterSchedule?.specialties ?? [];

    const formatted = formatEventsForWeekView(filteredEvents, rangeDate.start, weekStartDay);

    const hasFilter = providers.length > 0 || specialties.length > 0;

    return groupEventsByDayAndTime(formatted, hasFilter, weekStartDay);
  }, [
    filteredEvents,
    props.ScheduleSettingSequence?.filterSchedule,
    providers,
    rangeDate.start,
    weekStartDay,
  ]);

  useEffect(() => {
    if (selectedEventIds.length > 0) {
      const futureItemIds = formattedEvents.flatMap((event) => {
        const isEventSelected = event.items.some((item) => selectedEventIds.includes(item.id));

        if (!isEventSelected) {
          return [];
        }

        const futureItems = event.items.filter((item) => {
          const itemDate = moment(item.start_datetime);

          return itemDate.isSameOrAfter(moment(), "day");
        });

        return futureItems.map((item) => item.id);
      });

      setSelectedEventIds([...new Set([...selectedEventIds, ...futureItemIds])]);
    }
  }, [formattedEvents]);

  const filteredHolidays = useMemo(() => {
    if (selectedWeeks.length === 0) {
      return [];
    }

    const selectedDateRanges = getSelectedDateRanges(selectedWeeks, weekStartDay);
    const items = props.HolidaySettingSequence?.holiday?.items ?? [];

    return items
      .filter((item) => {
        const holidayDate = moment(item.dates[0]);

        return isDateInSelectedRanges(holidayDate, selectedDateRanges);
      })
      .map((item) => {
        const date = moment(item.dates[0]);

        return { date, name: `${date.format("MMM.D")} - ${item.detail}` };
      });
  }, [props.HolidaySettingSequence?.holiday, selectedWeeks, weekStartDay]);

  const fullDateText = useMemo(() => {
    const { start } = rangeDate;
    const end = rangeDate.end;

    return `${start.format("MMMM")} ${start.format("D")}-${end.format("D")}, ${start.format(
      "YYYY"
    )}`;
  }, [rangeDate]);

  const isSelectedAll = useMemo(() => {
    const ids = formattedEvents.flatMap((event) => event.items.map((item) => item.id));

    return selectedEventIds.length > 0 && ids.length === selectedEventIds.length;
  }, [formattedEvents, selectedEventIds]);

  const errorMessage = useMemo(() => {
    const error = props.errorMessage?.[SEQUENCE_NAME];
    const dupDsbList: Record<string, any>[] = error?.[0]?.duplicated_dsb_list ?? [];
    const isDupDsb = Array.isArray(error) && dupDsbList.length > 0;
    const providerNames = isDupDsb
      ? error.map((item) => item.provider_name as string).filter(Boolean)
      : [];

    if (isDupDsb) {
      const dupMessage =
        providerNames.length === 0
          ? {
              subtitle: "เนื่องจากช่วงเวลาซ้ำซ้อน กรุณาแก้ไขจากช่วงเวลาเดิม",
              title: "ไม่สามารถสร้างเวลาให้บริการใหม่ได้",
            }
          : {
              subtitle: `เนื่องจากผู้ให้บริการ [${providerNames.join(
                ", "
              )}]\nมีช่วงเวลาซ้ำซ้อน กรุณาแก้ไขจากช่วงเวลาเดิม`,
              title: "ไม่สามารถลงตารางผู้ให้บริการได้",
            };

      return {
        duplicate: dupMessage,
      };
    }

    return { error };
  }, [props.errorMessage]);

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      props.runSequence({
        sequence: "ScheduleSetting",
        action: ACTIONS.PICK_SLOT,
        selectedWeeks,
        slotInfo,
      });
    },
    [selectedWeeks]
  );

  const handleSelectEvent = useCallback(() => {
    // setEvents((prev) =>
    //   prev.map((e) => (e.id === event.id ? { ...e, isSelected: !e.is_selected } : e))
    // );
  }, []);

  const handleChangeSelectAll = useCallback(() => {
    if (isSelectedAll) {
      setSelectedEventIds([]);

      return;
    }

    const ids = formattedEvents.flatMap((event) => event.items.map((item) => item.id));

    setSelectedEventIds(ids);
  }, [formattedEvents, isSelectedAll]);

  const handlePreviousWeek = useCallback(() => {
    const currentIndex = filteredWeekRanges.findIndex((w) => selectedWeeks.includes(w.value));

    if (currentIndex <= 0) {
      return;
    }

    const prevWeek = filteredWeekRanges[currentIndex - 1];

    setRangeDate({
      end: prevWeek.endDate.clone(),
      start: prevWeek.startDate.clone(),
    });
    setSelectedWeeks([prevWeek.value]);
  }, [filteredWeekRanges, selectedWeeks]);

  const handleNextWeek = useCallback(() => {
    const currentIndex = filteredWeekRanges.findIndex((w) => selectedWeeks.includes(w.value));

    if (currentIndex >= filteredWeekRanges.length - 1) {
      return;
    }

    const nextWeek = filteredWeekRanges[currentIndex + 1];

    setRangeDate({
      end: nextWeek.endDate.clone(),
      start: nextWeek.startDate.clone(),
    });
    setSelectedWeeks([nextWeek.value]);
  }, [filteredWeekRanges, selectedWeeks]);

  const handleClosePopup = useCallback(() => {
    props.runSequence({ sequence: "ScheduleSetting", action: ACTIONS.REMOVE_DRAFT });
  }, []);

  const handleSaveSchedule: ModScheduleInfoProps["onSave"] = useCallback(
    async (data) => {
      await props.runSequence({
        sequence: "ScheduleSetting",
        action: ACTIONS.SAVE,
        data,
        holidays: props.HolidaySettingSequence?.holiday?.items ?? [],
        selectedWeeks,
      });
    },
    [props.HolidaySettingSequence?.holiday, selectedWeeks]
  );

  const handleDeleteSchedule: ModScheduleInfoProps["onDelete"] = useCallback(async (data) => {
    await props.runSequence({
      sequence: "ScheduleSetting",
      action: ACTIONS.DELETE,
      data,
    });
  }, []);

  const handlePublishSchedule = useCallback(async (data: number[], btnAction?: string) => {
    await props.runSequence({
      sequence: "ScheduleSetting",
      action: ACTIONS.PUBLISH,
      btnAction,
      data,
      onSuccess: () => {
        setSelectedEventIds([]);
      },
    });
  }, []);

  const handleEditSchedule: ModScheduleInfoProps["onEdit"] = useCallback(async (data) => {
    await props.runSequence({
      sequence: "ScheduleSetting",
      action: ACTIONS.EDIT,
      data,
    });
  }, []);

  const handleEditDSB: ModEditScheduleProps["onUpdateDSB"] = useCallback(async (data) => {
    await props.runSequence({
      sequence: "ScheduleSetting",
      action: ACTIONS.UPDATE_DSB,
      ...data,
    });
  }, []);

  const handleSaveProvider = useCallback((data: AddProviderParamsType) => {
    props.runSequence({
      sequence: "ScheduleSetting",
      action: ACTIONS.ADD_PROVIDER,
      ...data,
      onSuccess: () => {
        setOpenAddProvider(false);
        setSelectedEventIds([]);
      },
    });
  }, []);

  const handleChangeYear = useCallback((e: any, data: any) => {
    const updateYear = data.value as number;

    setYear(updateYear);

    props.setProp("ScheduleSettingSequence.year", updateYear, () => {
      props.runSequence({ sequence: "ScheduleSetting", action: ACTIONS.SEARCH });
    });
  }, []);

  const handleCheckboxEvent = useCallback(
    (ids: number[]) => {
      let updateIds = [...selectedEventIds];

      const hasEvent = selectedEventIds.some((id) => ids.includes(id));

      updateIds = hasEvent ? updateIds.filter((id) => !ids.includes(id)) : [...updateIds, ...ids];

      setSelectedEventIds(updateIds);
    },
    [selectedEventIds]
  );

  const handleClickPublish = useCallback(async () => {
    await handlePublishSchedule(selectedEventIds, BTN_ACTS.PUBLISH);
  }, [handlePublishSchedule, selectedEventIds]);

  const handleSelectProvider = useCallback(
    (value: any) => {
      const updateValue = value?.doctor_provider_id
        ? [{ id: value.doctor_provider_id, name: value.doctor_provider_name }]
        : null;

      props.setProp(
        "ScheduleSettingSequence.filterSchedule",
        {
          ...props.ScheduleSettingSequence?.filterSchedule,
          providers: updateValue,
          specialties: [],
        },
        () => {
          props.runSequence({ sequence: "ScheduleSetting", action: ACTIONS.SEARCH });
        }
      );
    },
    [props.ScheduleSettingSequence?.filterSchedule]
  );

  const handleSearch = useCallback(
    async (filter: FilterScheduleType) => {
      await props.setProp("ScheduleSettingSequence.filterSchedule", {
        ...props.ScheduleSettingSequence?.filterSchedule,
        ...filter,
      });

      await props.runSequence({ sequence: "ScheduleSetting", action: ACTIONS.SEARCH });
    },
    [props.ScheduleSettingSequence?.filterSchedule]
  );

  const handleSelectChat = useCallback((hn: string) => {
    const isMatchedHN = patientHNChatListRef.current.includes(hn);

    if (!isMatchedHN) {
      props.setProp("patientHNChatList", [...patientHNChatListRef.current, hn]);
    }
  }, []);

  return (
    <>
      <SnackMessage
        error={errorMessage.error}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <ModScheduleErrMessage
        open={!!errorMessage.duplicate}
        subtitle={errorMessage.duplicate?.subtitle || ""}
        title={errorMessage.duplicate?.title || ""}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <ModScheduleErrMessage
        open={!!props.successMessage?.[SEQUENCE_NAME]}
        subtitle=""
        title={props.successMessage?.[SEQUENCE_NAME]}
        onClose={() => {
          props.setProp(`successMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <Container
        style={{
          display: "flex",
          flexDirection: "column",
          fontSize: "0.95rem",
          height: "calc(100vh - 40px)",
          padding: "1rem",
        }}
        fluid
      >
        {/* Top Filter Bar */}
        <div
          style={{
            alignItems: "center",
            display: "flex",
            gap: "12px",
            height: "32px",
            marginBottom: "16px",
          }}
        >
          <Dropdown
            style={{ fontWeight: 600, minWidth: "100px" }}
            value={year}
            selection
            options={YEAR_OPTIONS}
            onChange={handleChangeYear}
          />

          <Dropdown
            style={{ minWidth: "100px" }}
            value={rangeWeek.start}
            selection
            options={weekRanges}
            onChange={(e, { value }) =>
              setRangeWeek((prev) => ({ ...prev, start: value as string }))
            }
          />

          <div style={{}}>To</div>

          <Dropdown
            style={{ minWidth: "100px" }}
            value={rangeWeek.end}
            selection
            options={weekRanges}
            onChange={(e, { value }) => setRangeWeek((prev) => ({ ...prev, end: value as string }))}
          />

          <Button
            style={{ backgroundColor: "rgba(255, 229, 229, 1)", margin: "0 0px 0 4px" }}
            disabled={isMultipleWeeks}
            size="mini"
            icon
            onClick={handlePreviousWeek}
          >
            <Icon name="chevron left" />
          </Button>

          <div
            style={{
              color: isMultipleWeeks ? "#B3B3B3" : "#3C3C3C",
              fontSize: "14px",
              minWidth: "150px",
              textAlign: "center",
            }}
          >
            {isMultipleWeeks ? "..." : fullDateText}
          </div>

          <Button
            style={{ backgroundColor: "rgba(255, 229, 229, 1)", margin: "0 4px 0 0px" }}
            disabled={isMultipleWeeks}
            size="mini"
            icon
            onClick={handleNextWeek}
          >
            <Icon name="chevron right" />
          </Button>

          <div style={{ flex: 1 }} />

          {props.isProvider && (
            <SearchProviderSpecialty
              onEvent={props.onEvent}
              divisionId={props.selectedDivision?.id}
              filter={props.ScheduleSettingSequence?.filterSchedule}
              searchedItemListWithKey={props.searchedItemListWithKey}
              specialtyOptions={props.masterOptions.specialty}
              onSearch={handleSearch}
            />
          )}

          <Checkbox
            checked={isSelectedAll}
            label={<label style={{ marginRight: "1rem" }}>Select All</label>}
            onChange={handleChangeSelectAll}
          />

          {props.isProvider ? (
            <Button
              style={{ ...styles.btnAdd, padding: "7px 14px" }}
              disabled={selectedEventIds.length === 0}
              size="small"
              onClick={() => {
                setOpenAddProvider(true);
              }}
            >
              <img
                style={{ height: "17px", marginRight: "0.5rem", width: "auto" }}
                alt="provider.icon"
                src={IMAGES.provider}
              />
              <Icon name="plus" />
              <label>Provider</label>
            </Button>
          ) : (
            <ButtonLoadCheck
              setProp={props.setProp}
              style={styles.btnAdd}
              paramKey={BTN_ACTS.PUBLISH}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PUBLISH]}
              onClick={handleClickPublish}
              title={
                <div style={{ alignItems: "center", display: "flex" }}>
                  <img
                    style={{
                      cursor: "pointer",
                      height: "24px",
                      marginRight: "0.5rem",
                      width: "auto",
                    }}
                    alt="published-active.icon"
                    src={IMAGES.publishedActive}
                  />
                  <label>Publish</label>
                </div>
              }
            />
          )}
        </div>

        {/* Main Content Area */}
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* Left Sidebar - Week List */}
          <WeekListSidebar
            events={props.ScheduleSettingSequence?.events}
            filteredWeekRanges={filteredWeekRanges}
            selectedWeeks={selectedWeeks}
            onDragUpdate={(weeks) => setSelectedWeeks(weeks)}
            onWeekSelect={(week, date) => setRangeDate(date)}
          />

          {/* Calendar Area */}
          <div
            style={{
              background: "white",
              borderRadius: "4px",
              flex: 1,
              minWidth: 0,
              padding: "0 12px",
            }}
          >
            <CalendarSchedule
              divisionId={props.selectedDivision?.id}
              events={formattedEvents}
              holidays={filteredHolidays}
              isMultipleWeeks={isMultipleWeeks}
              isProvider={props.isProvider}
              providerIds={providers}
              rangeDate={rangeDate}
              selectedEventIds={selectedEventIds}
              selectedWeeks={selectedWeeks}
              specialtyIds={props.ScheduleSettingSequence?.filterSchedule?.specialties ?? null}
              weekStartDay={props.ScheduleSettingSequence?.weekStartDay}
              year={year}
              onCheckbox={handleCheckboxEvent}
              onClose={handleClosePopup}
              onDelete={handleDeleteSchedule}
              onEdit={handleEditSchedule}
              onPublish={handlePublishSchedule}
              onSave={handleSaveSchedule}
              onSelectChat={handleSelectChat}
              onSelectEvent={handleSelectEvent}
              onSelectProvider={handleSelectProvider}
              onSelectSlot={handleSelectSlot}
              onUpdateDSB={handleEditDSB}
            />
          </div>
        </div>
      </Container>

      <ModAddProvider
        onEvent={props.onEvent}
        setProp={props.setProp}
        divisionId={props.selectedDivision?.id}
        events={formattedEvents}
        open={openAddProvider}
        selectedEventIds={selectedEventIds}
        selectedWeekRange={selectedWeekRange}
        buttonLoadCheck={props.buttonLoadCheck}
        searchedItemListWithKey={props.searchedItemListWithKey}
        onClose={() => {
          setOpenAddProvider(false);
        }}
        onPastDate={() => {
          setOpenModPastDate(true);
          setOpenAddProvider(false);
        }}
        onSave={handleSaveProvider}
      />

      <ModScheduleErrMessage
        open={openModPastDate}
        subtitle="เนื่องจากเลยวันเวลาให้บริการแล้ว"
        title="ไม่สามารถลงตารางผู้ให้บริการย้อนหลังได้"
        onClose={() => {
          setOpenModPastDate(false);
          setSelectedEventIds([]);
        }}
      />
    </>
  );
};

CardScheduleSetting.displayName = "CardScheduleSetting";

export default React.memo(CardScheduleSetting);
